import React, { useContext, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArchiveContext } from "../App.js";
import { SecurityEvent } from "../types.js";
import SEOMeta, { generateYearSEO } from "../components/SEOMeta.js";

const EventsPage: React.FC = () => {
  const { yearNum } = useParams<{ yearNum: string }>();
  const { getEventsByYear, isLoading: contextLoading } = useContext(ArchiveContext);
  const [localEvents, setLocalEvents] = useState<SecurityEvent[]>([]);
  const [localLoading, setLocalLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      if (yearNum) {
        setLocalLoading(true);
        const data = await getEventsByYear(Number(yearNum));
        setLocalEvents(data);
        setLocalLoading(false);
      }
    };
    fetch();
  }, [yearNum, getEventsByYear]);

  if (localLoading || contextLoading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center space-y-4">
        <div className="w-10 h-10 border-2 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
        <p className="text-[13px] text-slate-400">Opening Year Dossier...</p>
      </div>
    );
  }

  const displayYear =  `${Math.abs(Number(yearNum))} `;
  const canonicalUrl = `https://cyberannals.com/year/${yearNum}`;
  const seoData = generateYearSEO(displayYear, localEvents.length);

  return (
    <>
      <SEOMeta
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={canonicalUrl}
        ogType="website"
        ogImage={seoData.ogImage}
      />
      
      {/* Add structured data for Year page */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": `${displayYear} Cyber Security Events`,
            "description": seoData.description,
            "url": canonicalUrl,
            "mainEntity": {
              "@type": "ItemList",
              "numberOfItems": localEvents.length,
              "itemListElement": localEvents.map((event, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "item": {
                  "@type": "Article",
                  "headline": event.title,
                  "url": `https://cyberannals.com/event/${event.id}`
                }
              }))
            }
          })
        }}
        style={{ display: 'none' }}
      />
      <div className="space-y-12 py-6 max-w-4xl mx-auto px-4 md:px-6">
        <nav className="flex items-center gap-2 text-[14px] text-slate-400">
          <Link to="/decades" className="hover:text-white transition-colors">
            Archive
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] opacity-50"></i>
          <span className="text-white font-medium">Year: {displayYear}</span>
        </nav>

        <div className="space-y-3">
          <h1 className="text-5xl md:text-7xl font-bold text-white">
            {displayYear}
          </h1>
          <div className="h-1 w-24 bg-blue-500 rounded-full"></div>
          <p className="text-[14px] text-slate-500 pt-2">
            Records recovered from secure storage
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {localEvents.map((event, index) => (
            <Link
              key={event.id}
              to={`/event/${event.id}`}
              className="flex flex-col md:flex-row gap-6 p-6 bg-white/5 border border-white/10 rounded-2xl hover:border-blue-500/50 hover:bg-white/10 transition-all group"
            >
              <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-lg bg-slate-900 text-blue-400 group-hover:bg-blue-500 group-hover:text-white font-bold text-lg transition-all">
                {String(index + 1).padStart(2, "0")}
              </div>
              <div className="space-y-3 flex-grow">
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-blue-500 text-white text-[12px] font-medium rounded">
                    {event.attackType}
                  </span>
                  <span className="px-2 py-1 bg-white/10 text-slate-400 text-[12px] rounded border border-white/10">
                    {event.victim?.name || "Restricted"}
                  </span>
                </div>
                <h2 className="text-xl font-bold text-white group-hover:text-blue-400 transition-colors">
                  {event.title}
                </h2>
                <p className="text-[14px] text-slate-400 line-clamp-2 leading-relaxed">
                  {event.persuasiveStory}
                </p>
              </div>
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white/10 text-blue-400 group-hover:bg-blue-500 group-hover:text-white transition-all self-center">
                <i className="fa-solid fa-arrow-right text-[12px]"></i>
              </div>
            </Link>
          ))}
        </div>

        {localEvents.length === 0 && (
          <div className="py-16 text-center border border-dashed border-white/10 rounded-xl bg-white/5">
            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4 border border-white/10">
              <i className="fa-solid fa-magnifying-glass text-2xl text-slate-700"></i>
            </div>
            <p className="text-lg text-slate-400 mb-2">Zero Records Found</p>
            <p className="text-[14px] text-slate-600">
              The ledger contains no verified entries for {displayYear}.
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default EventsPage;
